import { clearCache } from "./useCachedGet";
import { clearDismissed } from "./needsReview";
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setLoading(false);
      if (window.location.pathname !== "/login" && !window.location.pathname.startsWith("/signup")) {
        window.location.href = "/login";
      }
      return;
    }
    try {
      const res = await api.get("/auth/me/");
      setUser(res.data);
    } catch (err) {
      // Token expired — try refresh
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        try {
          const res = await api.post("/auth/refresh/", { refresh: refreshToken });
          localStorage.setItem("access_token", res.data.access);
          if (res.data.refresh) {
            localStorage.setItem("refresh_token", res.data.refresh);
          }
          const userRes = await api.get("/auth/me/");
          setUser(userRes.data);
        } catch {
          logout();
        }
      } else {
        logout();
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (username, password) => {
    const res = await api.post("/auth/login/", { username, password });
    localStorage.setItem("access_token", res.data.access);
    localStorage.setItem("refresh_token", res.data.refresh);
    // A fresh sign-in shows the list of what is waiting, whatever was put away before (D-278).
    clearDismissed();
    const userRes = await api.get("/auth/me/");
    setUser(userRes.data);
    return userRes.data;
  };

  const logout = () => {
    clearCache();
    clearDismissed();
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
